import { apiGet, apiPost, apiPatch, apiDelete } from './client';
import type { UpdateProjectTemplatesInput } from './templates';

// ===== Types =====

export interface ProjectLabel {
  id: number;
  name: string | null;
  color: string | null;
  isSystem: boolean;
}

export interface Project {
  id: number;
  /** Phase 27 D-15: 3-char unique project identifier, NOT NULL in DB. */
  slug: string;
  name: string;
  fullName: string | null;
  description: string | null;
  state: string;
  isDefault: boolean;
  mandatoryTasks: boolean;
  allowExternalIntake: boolean;
  /**
   * 260727-gob — opt-in per-project flag: when true, ANY authenticated
   * internal user may read the board + task detail sub-resources and
   * create tasks/comments without being a project member. Defaults to
   * false (restriction ON).
   */
  openToAllInternal: boolean;
  defaultRate: number | null;
  hoursTotal: number | null;
  moneyTotal: number | null;
  /**
   * Phase 72 (currency-aware budgets) — the currency `moneyTotal` is
   * denominated in. Backend defaults to 'CZK' for legacy rows that predate
   * this column (see 72-01 `?? 'CZK'`).
   */
  moneyTotalCurrency?: 'CZK' | 'EUR' | 'USD';
  /**
   * Phase 72 Plan 02 — CZK-equivalent of `moneyTotal`, derived read-time
   * (provisional, open period) or from the immutable `project_budget_locks`
   * snapshot (locked, closed period). Detail response only.
   */
  moneyTotalCzk?: number | null;
  /** Phase 72 Plan 02 — true while the budget period is still open (the CZK
   * equivalent above is a live recompute, not yet locked). */
  moneyTotalProvisional?: boolean;
  /**
   * Phase 72 Plan 06 (CUR-09, D-07) — single project-level T&M/overage
   * billing currency. Per-role T&M rates + SLA balíček overage rates
   * (ProjectOverageRate) are converted to CZK at billing time using this
   * currency (balíček overage inherits `balicekCurrency` instead — see
   * that field). Backend defaults to 'CZK' for legacy rows.
   */
  tmBillingCurrency?: 'CZK' | 'EUR' | 'USD';
  startDate: string | null;
  endDate: string | null;
  customer: { id: number; name: string } | null;
  parent: { id: number; name: string } | null;
  projectManager: { id: number; name: string; email?: string } | null;
  labels?: ProjectLabel[];
  _count?: { children: number; assignments: number; entries: number };
  createdAt?: string | null;
  updatedAt?: string | null;
  appliedTemplateId: number | null;
  paymentTypeId: number | null;
  projectTypeId: number | null;
  taskTypeId: number | null;
  /**
   * UAT 2026-05-13 F-D4 — true when SLA Balíček allows hours beyond the
   * monthly cap. Drives OverageApprovalCard visibility on ProjectDetailPage.
   * Backend `projectDetailResponse` adds this field; older responses omit it.
   */
  overLimitAllowed?: boolean;
  /**
   * UAT 2026-05-13 F-D6 — balíček config columns surfaced on detail response
   * so the inline edit form can seed its inputs (consumption DTO omits
   * balicekAmount). Optional because non-balíček projects leave them NULL.
   */
  balicekHours?: number | null;
  balicekAmount?: string | null;
  balicekCurrency?: string | null;
  /**
   * Phase 72 Plan 04 (CUR-02/CUR-06) — provisional CZK equivalent of the
   * balíček monthly fee + its provisional flag, for the Hour Package card's
   * Monthly fee row. Detail response only; null when balicekAmount is null.
   * Always provisional for a foreign currency (the fee locks at
   * invoice-generate time, not here). See backend deriveBalicekAmountCzk.
   */
  balicekAmountCzk?: number | null;
  balicekAmountProvisional?: boolean;
  /**
   * Petra staging UAT 2026-05-18 — backend now surfaces these child rows on
   * detail so ProjectFormPage can pre-fill the Hour Package edit form
   * instead of asking the user to retype them on every save.
   */
  alertThresholds?: Array<{ id: number; percentage: number }>;
  overageRates?: Array<{ id: number; roleId: number; hourlyRate: string }>;
  /**
   * Phase 53 (Plan 53-04 / COVERAGE-08 config) — rollover policy and cap.
   * Surfaced on detail response by 53-02 so the edit form can seed the
   * rollover fields. Optional on non-balíček projects (column defaults 'none').
   */
  rolloverPolicy?: 'none' | 'monthly' | 'annual';
  rolloverCapHours?: number | null;
  /**
   * MKS-94 — billing terms, carried over from the lead on conversion and
   * editable afterwards by admin, finance or the project's PM. Billing type is
   * `paymentTypeId` above; `paymentType` / `paymentTerm` are resolved labels so
   * the form does not have to hit the admin-only template catalogue.
   * `contractFrequency` doubles as the SLA contract cadence (see the backend's
   * services/billingTerms.ts for the lead → project mapping).
   */
  contractFrequency?: string | null;
  contractUrl?: string | null;
  paymentTermId?: number | null;
  paymentTerm?: { id: number; name: string; days: number } | null;
  paymentType?: { id: number; name: string; slug: string } | null;
  /**
   * Phase 58 — the Fixed Price contract. `contractAmount` is a string
   * (DECIMAL(19,4) serialized via `.toString()`), not a number.
   */
  contractAmount?: string | null;
  contractCurrency?: string | null;
  /**
   * MKS-137 — the contract window, and the Fixed Price burn-down the detail
   * page's Contract & billing section shows: how much of `contractAmount` the
   * invoice ledger has already drawn and what is left. Both burn-down fields
   * are null off a Fixed Price project and while the flag is off — the backend
   * skips the ledger read entirely then. Strings, like `contractAmount`.
   */
  contractStart?: string | null;
  contractEnd?: string | null;
  contractInvoiced?: string | null;
  contractRemaining?: string | null;
  /**
   * Phase 62 (Plan 62-06, TBI-01) — explicit billable flag. True by default;
   * false hides the project from the To Be Invoiced queue and unmounts the
   * ToBeInvoicedCard on the detail page.
   */
  billable?: boolean;
  /**
   * 260727-czj — opt-in allowlist of ProjectRole ids permitted to move
   * tasks into in-progress columns. Empty/absent = restriction OFF (any
   * project member may move any task, current behavior).
   */
  inProgressAllowedRoleIds?: number[];
  /**
   * F3M-01 — when true, time entries cannot be logged on this project.
   * Defaults to false for new leaf projects; auto-set to true on container
   * projects (those with subprojects). Admins can toggle via the project form.
   */
  timeReportingDisabled?: boolean;
  /**
   * 260913-jpy — per-project opt-in for the task staleness emoji on Board
   * view, List view, and the task detail slide-over. Defaults to false
   * (feature OFF) for all existing projects. When true, `stalenessStaleDays`
   * / `stalenessGraveyardDays` override the frontend's global 5/15 ladder
   * for this project only — My Tasks is unaffected.
   */
  stalenessIndicatorEnabled?: boolean;
  stalenessStaleDays?: number;
  stalenessGraveyardDays?: number;
}

export interface ProjectListItem {
  id: number;
  /** Phase 27 D-15: 3-char unique project identifier, NOT NULL in DB. */
  slug: string;
  name: string;
  fullName: string | null;
  description: string | null;
  state: string;
  isDefault: boolean;
  defaultRate: number | null;
  hoursTotal: number | null;
  moneyTotal: number | null;
  startDate: string | null;
  endDate: string | null;
  customer: { id: number; name: string } | null;
  parent: { id: number; name: string } | null;
  projectManager: { id: number; name: string } | null;
  // Phase 20 BL-01: list endpoint now exposes appliedTemplateId so
  // ProjectTemplateEditPage can derive `inUseCount` for the TPL-06 (D-05)
  // disabled-soft-delete tooltip without an extra request.
  appliedTemplateId: number | null;
  /**
   * Phase 41 Plan 41-10 (TYPE-03 surfacing): drives the TypePill column on
   * ProjectListPage. Maps to the SLA / Mixed / Default tone via
   * `projectTypeTone()`. Null on sidebar rows (memberOf=true branch);
   * populated from the `Project.projectTypeId` column on the full list.
   */
  projectTypeId: number | null;
  /**
   * UAT 2026-05-13 — paymentTypeId on list rows so InvoiceGeneratePage can
   * split its project picker into "SLA Balíček" vs "Hourly" sections.
   * Backend `projectSummarySchema` declares the field; null on rows where
   * no payment template is assigned (pre-v7.0 baseline projects).
   */
  paymentTypeId: number | null;
  /** Phase 23 D-15/D-17: present on memberOf=true responses; absent otherwise. */
  parentId?: number | null;
  /** Phase 23 D-19: true for membered projects, false for ghost ancestors. */
  isMember?: boolean;
  /**
   * Quick task 260804-keu — the backend list response already includes this
   * (projectSummarySchema). SetupProjectsModal filters each combobox section
   * by billable === true / false.
   */
  billable?: boolean;
}

/**
 * MKS-358 — an assignment is either `confirmed` (the person works on the
 * project and has the access that implies) or `tentative` (a plan: it books
 * capacity and grants nothing).
 */
export type AssignmentStatus = 'confirmed' | 'tentative';

/** One per-month override of an assignment's base allocation. */
export interface AssignmentAllocationOverride {
  /** `YYYY-MM`. */
  month: string;
  share: number | null;
  hours: number | null;
}

/**
 * One allocation figure. The backend takes EXACTLY ONE of the two — sending
 * both, or neither, is a 400 — because a share and an hours figure disagree
 * the moment the person's FTE changes and there is no rule for which wins.
 */
export interface AssignmentAllocationInput {
  /** Fraction of the person's FTE, (0, 1]. */
  share?: number | null;
  /** Hours a month. */
  hours?: number | null;
}

export interface ProjectAssignment {
  id: number;
  userId: number;
  projectId: number;
  startDate: string | null;
  endDate: string | null;
  billingRate: number | null;
  // Phase 70 (currency-aware billing rates) — the currency the billingRate
  // amount is denominated in. Backend defaults to 'CZK' for legacy rows
  // that predate this column (see projectController.ts `?? 'CZK'`).
  billingRateCurrency?: 'CZK' | 'EUR' | 'USD';
  hourlyCosts: number | null;
  userInvestment: number | null;
  roleName: string | null;
  // MKS-358 — the staffing half of an assignment. Optional because a response
  // served while `mks_358_tentative_assignments` is off carries today's shape;
  // the editor then falls back to its pre-MKS-358 behaviour rather than
  // rendering an empty status.
  status?: AssignmentStatus;
  /** Fraction of the person's FTE, (0, 1]. Null when the allocation is hours. */
  allocationShare?: number | null;
  /** Hours a month. Null when the allocation is a share. */
  allocationHours?: number | null;
  /** Per-month overrides of the base allocation, oldest month first. */
  allocations?: AssignmentAllocationOverride[];
  user: {
    id: number;
    name: string;
    email: string;
    defaultHourlyCosts?: number | null;
    // Quick 260915-i0x — avatar color slot for the AssigneePicker rows.
    avatarColorSeq?: number | null;
    // MKS-203 — relative API path to the effective avatar, or null.
    avatarUrl?: string | null;
  } | null;
}

export interface Customer {
  id: number;
  name: string;
  projectCount: number;
}

export interface CreateProjectInput {
  name: string;
  /** Phase 27 D-11: 3-char admin override; auto-derived from name when omitted. */
  slug?: string;
  fullName?: string;
  description?: string;
  customerId?: number;
  parentId?: number;
  projectManagerId?: number;
  startDate?: string;
  endDate?: string;
  defaultRate?: number;
  hoursTotal?: number;
  moneyTotal?: number;
  // Phase 72 (currency-aware budgets) — persisted alongside moneyTotal;
  // backend validates against CZK/EUR/USD (400 otherwise). Defaults to
  // 'CZK' server-side when omitted.
  moneyTotalCurrency?: 'CZK' | 'EUR' | 'USD';
  isDefault?: boolean;
  mandatoryTasks?: boolean;
  allowExternalIntake?: boolean;
  // 260727-gob — opt-in open-board flag at create time. Defaults to false
  // (restriction ON) server-side when omitted.
  openToAllInternal?: boolean;
  labelIds?: number[];
  projectTemplateId?: number;
  // Phase 41 Plan 41-10 (Surface 1) — template trio. Backend createProjectBody
  // (Phase 35 TPL-01) already accepts these as ['number', 'null']; null is
  // serialized as a deliberate "no template chosen" signal.
  paymentTypeId?: number | null;
  projectTypeId?: number | null;
  taskTypeId?: number | null;
  // Phase 36 (Plan 36-05) — SLA Support contract + coverage. Required by
  // service-layer gate when projectTypeId === SLA_SUPPORT_PROJECT_TYPE_ID.
  contractStart?: string; // ISO date (YYYY-MM-DD)
  contractEnd?: string;
  contractFrequency?: 'monthly' | 'quarterly' | 'yearly' | 'one_off' | 'perpetual';
  coverageEntries?: Array<{
    tz: string;
    preset: 'business_hours' | 'business_hours_extended' | 'business_24_5' | 'business_24_7';
    holidayCountry: string;
    orderIndex?: number;
  }>;
  // Phase 37 (Plan 37-08 / PAY-01..03) — SLA balíček satellite fields.
  // Required by service-layer gate when paymentTypeId === SLA_BALICEK_PAYMENT_TYPE_ID.
  balicekHours?: number;
  balicekAmount?: string;
  balicekCurrency?: 'CZK' | 'EUR' | 'USD';
  // Phase 37 (Plan 37-08) — SLA balíček optional behavior:
  //   overLimitAllowed — when false, dispatch blocks new tasks once the
  //     monthly package is exhausted; when true, overage continues and is
  //     billed via overageRates (per-role) — overageRates UI deferred.
  //   alertThresholds — percentage points (1..200) of monthly package
  //     consumption that trigger notifications; e.g. [{percentage: 80}]
  //     fires at 80 % of balicekHours used.
  overLimitAllowed?: boolean;
  alertThresholds?: Array<{ percentage: number }>;
  // Phase 37 (Plan 37-08 / PAY-03 / PAY-11) — per-role hourly rate for
  // hours that exceed the monthly balíček. Required (≥1) when
  // overLimitAllowed=true. hourlyRate is a decimal string (max 2 places).
  overageRates?: Array<{ roleId: number; hourlyRate: string }>;
  // Phase 53 (Plan 53-04 / COVERAGE-08) — rollover policy + cap for SLA
  // Balíček. Optional; defaults handled by backend (policy='none', cap=null).
  rolloverPolicy?: 'none' | 'monthly' | 'annual';
  rolloverCapHours?: number | null;
  // Phase 38 (Plan 38-05) — SLA Task priority tiers. Required by
  // service-layer gate when taskTypeId === SLA_TASK_TYPE_ID (≥1 tier).
  // Target durations are minutes (1..100_000); warning thresholds are
  // percentages (1..200) — 1-10 entries each.
  slaTiers?: Array<{
    name: string;
    orderIndex: number;
    responseTargetMinutes: number;
    resolutionTargetMinutes: number;
    responseWarningThresholds: number[];
    resolutionWarningThresholds: number[];
  }>;
  // Phase 62 (Plan 62-06, TBI-01) — explicit billable flag.
  billable?: boolean;
  // F3M-01 — disable hour reporting on create (defaults to false in service).
  timeReportingDisabled?: boolean;
  // Phase 72 Plan 06 (CUR-09, D-07) — single project-level T&M billing
  // currency. Defaults to 'CZK' server-side when omitted.
  tmBillingCurrency?: 'CZK' | 'EUR' | 'USD';
  // 260913-jpy — per-project staleness indicator opt-in + day thresholds on
  // create. The two day counts must be supplied together (service-layer
  // gate); omitting all three defaults to disabled at the database defaults.
  stalenessIndicatorEnabled?: boolean;
  stalenessStaleDays?: number;
  stalenessGraveyardDays?: number;
}

export interface UpdateProjectInput {
  name?: string;
  fullName?: string;
  description?: string;
  customerId?: number | null;
  parentId?: number | null;
  projectManagerId?: number | null;
  startDate?: string | null;
  endDate?: string | null;
  defaultRate?: number | null;
  hoursTotal?: number | null;
  moneyTotal?: number | null;
  // Phase 72 (currency-aware budgets) — modeled as never-null (mirrors the
  // 72-01 backend column, which is NOT NULL DEFAULT 'CZK' with no "unset"
  // state), unlike balicekCurrency/contractCurrency below.
  moneyTotalCurrency?: 'CZK' | 'EUR' | 'USD';
  // Phase 72 Plan 06 (CUR-09, D-07) — modeled as never-null (mirrors the
  // backend column, which is NOT NULL DEFAULT 'CZK' with no "unset" state).
  tmBillingCurrency?: 'CZK' | 'EUR' | 'USD';
  isDefault?: boolean;
  mandatoryTasks?: boolean;
  allowExternalIntake?: boolean;
  // 260727-gob — opt-in open-board flag. Sparse update: undefined = no-op;
  // false turns the restriction back ON.
  openToAllInternal?: boolean;
  // Phase 62 (Plan 62-06, TBI-01) — explicit billable flag.
  billable?: boolean;
  // 260727-czj — opt-in allowlist of ProjectRole ids permitted to move
  // tasks into in-progress columns. Sparse update: undefined = no-op;
  // pass [] to clear the allowlist (turn the restriction OFF).
  inProgressAllowedRoleIds?: number[];
  // F3M-01 — disable hour reporting (sparse update: undefined = no-op).
  timeReportingDisabled?: boolean;
  // 260913-jpy — per-project staleness indicator opt-in + day thresholds.
  // Sparse update: undefined = no-op on that field.
  stalenessIndicatorEnabled?: boolean;
  stalenessStaleDays?: number;
  stalenessGraveyardDays?: number;
  labelIds?: number[];
  // UAT 2026-05-13 F-D6 — post-creation balíček config edit. Backend
  // PATCH /api/projects/:id now accepts these alongside the long-standing
  // basic fields; service-layer atomically updates Project columns +
  // delete-and-recreate the alertThresholds + overageRates child tables.
  balicekHours?: number | null;
  balicekAmount?: string | null;
  balicekCurrency?: 'CZK' | 'EUR' | 'USD' | null;
  overLimitAllowed?: boolean;
  alertThresholds?: Array<{ percentage: number }>;
  overageRates?: Array<{ roleId: number; hourlyRate: string }>;
  // Phase 53 (Plan 53-04 / COVERAGE-08) — rollover policy + cap for SLA
  // Balíček edit. Optional; backend ignores when key absent.
  rolloverPolicy?: 'none' | 'monthly' | 'annual';
  rolloverCapHours?: number | null;
  // MKS-251 — the template trio is editable on the project edit form now.
  // There is still only one write path: the backend hands a real change to
  // `updateProjectTemplates`, so the validation, the audit row and the
  // assignment side effects stay in one service. The route gates these three
  // fields on admin / finance / the project's PM.
  paymentTypeId?: number | null;
  projectTypeId?: number | null;
  taskTypeId?: number | null;
  // MKS-251 — Fixed Price contract fields, so a switch onto Fixed Price can
  // carry its required amount + currency in the same request.
  contractAmount?: string | null;
  contractCurrency?: 'CZK' | 'EUR' | 'USD' | null;
}

export interface CreateAssignmentInput {
  userId: number;
  startDate?: string;
  endDate?: string;
  billingRate?: number;
  // Phase 70 (currency-aware billing rates) — persisted alongside
  // billingRate; backend validates against CZK/EUR/USD (400 otherwise).
  billingRateCurrency?: 'CZK' | 'EUR' | 'USD';
  hourlyCosts?: number;
  userInvestment?: number;
  roleName?: string;
  // MKS-358 — the staffing half. Dropped server-side while
  // `mks_358_tentative_assignments` is off, so sending it is always safe.
  status?: AssignmentStatus;
  /** `null` CLEARS the allocation; omitting the key leaves it alone. */
  allocation?: AssignmentAllocationInput | null;
}

export interface UpdateAssignmentInput {
  startDate?: string | null;
  endDate?: string | null;
  billingRate?: number | null;
  billingRateCurrency?: 'CZK' | 'EUR' | 'USD';
  hourlyCosts?: number | null;
  userInvestment?: number | null;
  roleName?: string | null;
  // MKS-358 — see CreateAssignmentInput.
  status?: AssignmentStatus;
  allocation?: AssignmentAllocationInput | null;
}

// ===== Label API =====

export async function getLabels(): Promise<ProjectLabel[]> {
  const res = await apiGet<{ data: ProjectLabel[] }>('/labels');
  return res.data;
}

export interface AdminLabel {
  id: number;
  name: string | null;
  color: string | null;
  isSystem: boolean;
  description: string | null;
  projectCount: number;
}

export async function getLabelsAdmin(): Promise<AdminLabel[]> {
  const res = await apiGet<{ data: AdminLabel[] }>('/labels/admin');
  return res.data;
}

export async function createLabelAdmin(input: { name: string; color?: string; description?: string; isSystem?: boolean }): Promise<ProjectLabel> {
  const res = await apiPost<{ data: ProjectLabel }>('/labels', input);
  return res.data;
}

export async function updateLabelAdmin(id: number, input: { name?: string; color?: string | null; description?: string | null; isSystem?: boolean }): Promise<ProjectLabel> {
  const res = await apiPatch<{ data: ProjectLabel }>(`/labels/${id}`, input);
  return res.data;
}

export async function deleteLabelAdmin(id: number): Promise<void> {
  await apiDelete<{ data: { success: boolean } }>(`/labels/${id}`);
}

// ===== Project API =====

export interface ProjectFilters {
  state?: string;
  customerId?: number;
  projectManagerId?: number;
  search?: string;
  onlyParents?: boolean;
  onlyMine?: boolean;
  memberOf?: boolean; // Phase 23 D-13: PM-OR-Assignment membership for sidebar
  labelIds?: number[];
  includeArchived?: boolean;
}

export async function getProjects(filters?: ProjectFilters): Promise<ProjectListItem[]> {
  const params = new URLSearchParams();
  if (filters?.state) params.set('state', filters.state);
  if (filters?.customerId) params.set('customerId', String(filters.customerId));
  if (filters?.projectManagerId) params.set('projectManagerId', String(filters.projectManagerId));
  if (filters?.search) params.set('search', filters.search);
  if (filters?.onlyParents) params.set('onlyParents', 'true');
  if (filters?.onlyMine) params.set('onlyMine', 'true');
  if (filters?.memberOf) params.set('memberOf', 'true');
  if (filters?.labelIds && filters.labelIds.length > 0) params.set('labelIds', filters.labelIds.join(','));
  if (filters?.includeArchived) params.set('includeArchived', 'true');

  const query = params.toString();
  const endpoint = `/projects${query ? `?${query}` : ''}`;
  const res = await apiGet<{ data: ProjectListItem[] }>(endpoint);
  return res.data;
}

export async function getProject(id: number): Promise<Project> {
  const res = await apiGet<{ data: Project }>(`/projects/${id}`);
  return res.data;
}

// F-10 (2026-05-13 UAT batch Phase 3) — JR Approve modal role dropdown.
// Backend returns ProjectCapacityNeed.roleId distinct, falling back to
// the global ProjectRole catalog when the project has no needs yet.
export interface ProjectRoleOption {
  id: number;
  name: string;
  code: string | null;
}

export async function getProjectRoles(id: number): Promise<ProjectRoleOption[]> {
  const res = await apiGet<{ data: ProjectRoleOption[] }>(`/projects/${id}/roles`);
  return res.data;
}

/**
 * 260727-gob — GET /api/projects/open-boards. Reachable by ANY authenticated
 * internal user (no membership gate). Response shape is intentionally
 * limited to { id, name } — backs the Report-an-issue page's open-boards list.
 */
export interface OpenBoardProject {
  id: number;
  name: string | null;
}

export async function getOpenBoards(): Promise<OpenBoardProject[]> {
  const res = await apiGet<{ data: OpenBoardProject[] }>('/projects/open-boards');
  return res.data;
}

/**
 * MKS-477 — one row of the Tasks page's "All tasks" tab (GET /api/projects/task-boards).
 * Mirrors the backend `TaskBoardRow`. `lastActivityAt` is the later of the latest task update
 * and the latest live comment; `state` is returned for finished projects too (the page hides them).
 */
export interface TaskBoardSummary {
  id: number;
  name: string | null;
  fullName: string | null;
  customerName: string | null;
  state: string | null;
  openTaskCount: number;
  lastActivityAt: string | null;
}

/** MKS-477 — projects the caller can read that hold at least one live task. */
export async function getTaskBoards(): Promise<TaskBoardSummary[]> {
  const res = await apiGet<{ data: TaskBoardSummary[] }>('/projects/task-boards');
  return res.data;
}

export async function getChildProjects(id: number): Promise<ProjectListItem[]> {
  const res = await apiGet<{ data: ProjectListItem[] }>(`/projects/${id}/children`);
  return res.data;
}

export interface ProjectWithDepth extends ProjectListItem {
  depth: number;
  labels?: { id: number; name: string | null }[];
}

export async function getDescendantProjects(id: number): Promise<ProjectWithDepth[]> {
  const res = await apiGet<{ data: ProjectWithDepth[] }>(`/projects/${id}/descendants`);
  return res.data;
}

export interface ProjectAncestor {
  id: number;
  /** Phase 27 D-15: 3-char unique project identifier. */
  slug: string;
  name: string | null;
  fullName: string | null;
}

export async function getAncestorProjects(id: number): Promise<ProjectAncestor[]> {
  const res = await apiGet<{ data: ProjectAncestor[] }>(`/projects/${id}/ancestors`);
  return res.data;
}

export async function createProject(
  input: CreateProjectInput,
): Promise<{ id: number; slug: string; name: string; fullName: string | null; state: string }> {
  const res = await apiPost<{
    data: { id: number; slug: string; name: string; fullName: string | null; state: string };
  }>('/projects', input);
  return res.data;
}

/**
 * UAT 2026-05-13 — fetch a free 3-char slug for the given project name.
 *
 * Reuses the Phase 27 D-05 collision resolver on the backend; respects
 * Phase 27 D-01 (soft-deleted projects still hold their slug). Returned
 * slug is guaranteed unique at SELECT time, but a P2002 race is still
 * theoretically possible at INSERT time on concurrent creates.
 */
export async function suggestProjectSlug(name: string): Promise<string> {
  const res = await apiGet<{ data: { slug: string } }>(
    `/projects/suggest-slug?name=${encodeURIComponent(name)}`,
  );
  return res.data.slug;
}

export async function updateProject(
  id: number,
  input: UpdateProjectInput,
): Promise<{ id: number; slug: string; name: string; fullName: string | null; state: string }> {
  const res = await apiPatch<{
    data: { id: number; slug: string; name: string; fullName: string | null; state: string };
  }>(`/projects/${id}`, input);
  return res.data;
}

/**
 * Phase 35 — PATCH /api/projects/:id/templates (TPL-04, TPL-05).
 *
 * Lives in projects.ts (not templates.ts) because it's a project mutation —
 * it changes Project.paymentTypeId / projectTypeId / taskTypeId.
 *
 * MKS-251 removed the TPL-04 lock, so this no longer answers 409
 * TEMPLATES_LOCKED. The project edit form writes the trio through
 * `updateProject` instead; this stays for the admin-only route.
 */
export async function updateProjectTemplates(
  id: number,
  input: UpdateProjectTemplatesInput,
): Promise<{ success: boolean }> {
  const res = await apiPatch<{ data: { success: boolean } }>(
    `/projects/${id}/templates`,
    input,
  );
  return res.data;
}

/**
 * MKS-94 — billing terms on the project (admin, finance or the project's PM).
 *
 * Deliberately NOT a field of `UpdateProjectInput`: the main
 * PATCH /api/projects/:id is manager+admin, which is the wrong audience for
 * commercial terms. Billing type (`paymentTypeId`) is also absent — it stays on
 * `updateProjectTemplates`, which enforces the template lock.
 *
 * Omit a field to leave it alone; send `null` to clear it.
 */
export interface UpdateProjectBillingTermsInput {
  paymentTermId?: number | null;
  contractFrequency?: string | null;
  contractUrl?: string | null;
}

export interface ProjectBillingTerms {
  id: number;
  paymentTypeId: number | null;
  paymentType: { id: number; name: string; slug: string } | null;
  paymentTermId: number | null;
  paymentTerm: { id: number; name: string; days: number } | null;
  contractFrequency: string | null;
  contractUrl: string | null;
}

export async function updateProjectBillingTerms(
  id: number,
  input: UpdateProjectBillingTermsInput,
): Promise<ProjectBillingTerms> {
  const res = await apiPatch<{ data: ProjectBillingTerms }>(
    `/projects/${id}/billing-terms`,
    input,
  );
  return res.data;
}

export async function deleteProject(id: number): Promise<void> {
  await apiDelete<{ data: { success: boolean } }>(`/projects/${id}`);
}

export async function transitionProject(
  id: number,
  action: string,
  reason?: string,
): Promise<{ id: number; slug: string; name: string; state: string }> {
  const res = await apiPost<{ data: { id: number; slug: string; name: string; state: string } }>(
    `/projects/${id}/${action}`,
    reason ? { reason } : undefined,
  );
  return res.data;
}

/**
 * Quick 260805-gmk — POST /api/projects/merge (admin only). Moves ALL tasks
 * from sourceProjectId into targetProjectId; optionally archives the source
 * afterward. On a name/code conflict the backend returns 422 with
 * `details: { code: 'project_merge_conflict', conflicts }` — apiPost
 * surfaces this via `ApiClientError`.
 */
export interface MergeProjectsInput {
  sourceProjectId: number;
  targetProjectId: number;
  archiveSource: boolean;
  /** Fold true duplicates (name AND code hit the same target task) into the
   *  target's task instead of blocking the merge. */
  mergeDuplicates?: boolean;
}

export interface MergeProjectsResult {
  movedTaskCount: number;
  mergedDuplicateCount: number;
  archived: boolean;
}

export async function mergeProjects(input: MergeProjectsInput): Promise<MergeProjectsResult> {
  const res = await apiPost<{ data: MergeProjectsResult }>('/projects/merge', input);
  return res.data;
}

// ===== Assignment API =====

export async function getProjectAssignments(projectId: number): Promise<ProjectAssignment[]> {
  const res = await apiGet<{ data: ProjectAssignment[] }>(`/projects/${projectId}/assignments`);
  return res.data;
}

export async function createAssignment(projectId: number, input: CreateAssignmentInput): Promise<ProjectAssignment> {
  const res = await apiPost<{ data: ProjectAssignment }>(`/projects/${projectId}/assignments`, input);
  return res.data;
}

export async function updateAssignment(projectId: number, assignmentId: number, input: UpdateAssignmentInput): Promise<ProjectAssignment> {
  const res = await apiPatch<{ data: ProjectAssignment }>(`/projects/${projectId}/assignments/${assignmentId}`, input);
  return res.data;
}

/**
 * MKS-358 — confirm one tentative assignment. The id in the path is the whole
 * request: there is nothing else to send, and nothing else about the
 * assignment moves, so the confirm button cannot change dates or rates by
 * accident.
 */
export async function confirmAssignment(projectId: number, assignmentId: number): Promise<ProjectAssignment> {
  const res = await apiPost<{ data: ProjectAssignment }>(`/projects/${projectId}/assignments/${assignmentId}/confirm`);
  return res.data;
}

export async function deleteAssignment(projectId: number, assignmentId: number): Promise<void> {
  await apiDelete<{ data: { success: boolean } }>(`/projects/${projectId}/assignments/${assignmentId}`);
}

// ===== Customer API =====

export async function getCustomers(search?: string): Promise<Customer[]> {
  const endpoint = search ? `/customers?search=${encodeURIComponent(search)}` : '/customers';
  const res = await apiGet<{ data: Customer[] }>(endpoint);
  return res.data;
}

export async function getCustomer(id: number): Promise<Customer> {
  const res = await apiGet<{ data: Customer }>(`/customers/${id}`);
  return res.data;
}

export async function createCustomer(name: string): Promise<Customer> {
  const res = await apiPost<{ data: Customer }>('/customers', { name });
  return res.data;
}

export async function updateCustomer(id: number, name: string): Promise<Customer> {
  const res = await apiPatch<{ data: Customer }>(`/customers/${id}`, { name });
  return res.data;
}

export async function deleteCustomer(id: number): Promise<void> {
  await apiDelete<{ data: { success: boolean } }>(`/customers/${id}`);
}

// ===== Statistics Types =====

/**
 * MKS-332 — the cost-side fields are OPTIONAL. The API omits `laborCosts`,
 * `expenses`, `totalCosts` and the whole `profit` block for anyone who is not
 * admin or finance (absent, not null, so `undefined` is the honest type), and
 * a surface must check before rendering rather than assume 0.
 */
export interface PeriodStats {
  laborCosts?: number;
  totalBilling: number;
  invoicedBilling: number;
  /**
   * MKS-332 follow-up — Paid. Optional only because it arrives with the
   * backend that carries it; it is NOT a cost field and is never redacted.
   */
  paidBilling?: number;
  expenses?: number;
  extraGains: number;
  totalCosts?: number;
  hours: number;
}

export interface ProjectStatistics {
  wholeReport: PeriodStats;
  lastMonth: PeriodStats;
  profit?: {
    wholeReport: number;
    wholeReportPercentage: number;
    lastMonth: number;
    lastMonthPercentage: number;
  };
}

export interface InvestorSummary {
  investorId: number | null;
  investorName: string;
  hours: number;
  investmentRate: number | null;
  totalInvestment: number;
  sharePercent: number;
}

export interface InvestmentSummary {
  investors: InvestorSummary[];
  grandTotal: {
    hours: number;
    totalInvestment: number;
  };
}

export interface SubprojectStats {
  projectId: number;
  projectName: string;
  hours: number;
  totalBilling: number;
  invoicedBilling: number;
  // MKS-332 — omitted for anyone who is not admin or finance.
  laborCosts?: number;
  expenses?: number;
  totalCosts?: number;
  profit?: number;
  profitPercentage?: number;
  depth: number;
}

export interface SubprojectStatistics {
  subprojects: SubprojectStats[];
  grandTotal: {
    hours: number;
    totalBilling: number;
    invoicedBilling: number;
    // MKS-332 — omitted for anyone who is not admin or finance.
    laborCosts?: number;
    expenses?: number;
    totalCosts?: number;
    profit?: number;
    profitPercentage?: number;
  };
}

// ===== Statistics API =====

export async function getProjectStatistics(projectId: number): Promise<ProjectStatistics> {
  const res = await apiGet<{ data: ProjectStatistics }>(`/projects/${projectId}/statistics`);
  return res.data;
}

export async function getInvestmentSummary(projectId: number): Promise<InvestmentSummary> {
  const res = await apiGet<{ data: InvestmentSummary }>(`/projects/${projectId}/investment-summary`);
  return res.data;
}

export async function getSubprojectStatistics(projectId: number): Promise<SubprojectStatistics> {
  const res = await apiGet<{ data: SubprojectStatistics }>(`/projects/${projectId}/subproject-statistics`);
  return res.data;
}
